// frontend/src/components/VideoQuestion.jsx
import React, { useState, useEffect, useRef } from 'react';
import { showWarning, showInfo, showError, showSuccess } from '../utils/toastUtils';
import { saveVideoSelection, getVideoSelections, resetVideoSelections } from '../services/api';
import { getTranslation } from '../i18n/translations';
import { useLanguage } from '../contexts/LanguageContext';
import { RefreshIcon } from '@heroicons/react/outline';

const VideoQuestion = ({
    currentItem,
    accessToken,
    onAnswerComplete,
    onSubmitAssessment,
    totalQuestions,
    answeredCount,
    autoSubmitDelay = 2000,
    assessmentId,
}) => {
    const [selections, setSelections] = useState({});
    const [completedSelections, setCompletedSelections] = useState([]);
    const [isComplete, setIsComplete] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [autoSubmitCountdown, setAutoSubmitCountdown] = useState(null);

    const { language } = useLanguage();
    const t = (key, replacements) => getTranslation(language, key, replacements);

    const autoSubmitTriggeredRef = useRef(false);
    const countdownIntervalRef = useRef(null);

    const allQuestionsAnswered =
        totalQuestions > 0 && answeredCount >= totalQuestions;

    // ============================================
    // AUTO-SUBMIT
    // ============================================
    useEffect(() => {
        if (!allQuestionsAnswered || autoSubmitTriggeredRef.current || isSubmitting) return;

        autoSubmitTriggeredRef.current = true;

        const secondsTotal = Math.ceil(autoSubmitDelay / 1000);
        setAutoSubmitCountdown(secondsTotal);

        let remaining = secondsTotal;
        countdownIntervalRef.current = setInterval(() => {
            remaining -= 1;
            if (remaining <= 0) {
                clearInterval(countdownIntervalRef.current);
                setAutoSubmitCountdown(null);
                triggerSubmit();
            } else {
                setAutoSubmitCountdown(remaining);
            }
        }, 1000);

        return () => {
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
    }, [allQuestionsAnswered, autoSubmitDelay]);

    const triggerSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            if (onSubmitAssessment) await onSubmitAssessment();
        } catch (err) {
            console.error('Auto-submit error:', err);
            showError(t('test.submit_error') || 'Failed to submit assessment');
            autoSubmitTriggeredRef.current = false;
            setIsSubmitting(false);
        }
    };

    const handleManualSubmit = async () => {
        if (isSubmitting) return;
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        setAutoSubmitCountdown(null);
        await triggerSubmit();
    };

    // ============================================
    // RESET — only used in full mode (3+ options)
    // ============================================
    const handleReset = async () => {
        if (isResetting || isSubmitting) return;

        if (completedSelections.length === 0) {
            showInfo(t('test.nothing_to_reset') || 'No selections to reset');
            return;
        }

        const confirmed = window.confirm(
            t('test.confirm_reset') || 'Reset all rankings for this question?'
        );
        if (!confirmed) return;

        setIsResetting(true);
        try {
            await resetVideoSelections(accessToken, currentItem.id);
            setSelections({});
            setCompletedSelections([]);
            setIsComplete(false);

            autoSubmitTriggeredRef.current = false;
            setAutoSubmitCountdown(null);

            if (onAnswerComplete) onAnswerComplete(null);
            showSuccess(t('test.reset_success') || '✅ Selections reset!');
        } catch (error) {
            console.error('Reset error:', error);
            showError(t('test.reset_error') || 'Failed to reset selections');
        } finally {
            setIsResetting(false);
        }
    };

    // ============================================
    // VIDEO URL
    // ============================================
    const getVideoEmbedUrl = (url) => {
        if (!url) return null;
        if (url.includes('drive.google.com')) {
            const match = url.match(/\/d\/([^/]+)/);
            if (match && match[1]) return `https://drive.google.com/file/d/${match[1]}/preview?autoplay=1`;
            const idMatch = url.match(/id=([^&]+)/);
            if (idMatch && idMatch[1]) return `https://drive.google.com/file/d/${idMatch[1]}/preview?autoplay=1`;
        }
        return url;
    };

    const S3_BASE = 'https://pc-assessmentreports.s3.ap-south-1.amazonaws.com/assessment_files';

    const getVideoUrl = (item, assessmentId) => {
        if (!item) return null;

        if (item.video_name && assessmentId) {
            return `${S3_BASE}/${assessmentId}/videos/${item.video_name}`;
        }

        const url = item.text || '';
        if (url.includes('drive.google.com')) {
            const match = url.match(/\/d\/([^/]+)/);
            if (match && match[1]) return `https://drive.google.com/file/d/${match[1]}/preview?autoplay=1`;
            const idMatch = url.match(/id=([^&]+)/);
            if (idMatch && idMatch[1]) return `https://drive.google.com/file/d/${idMatch[1]}/preview?autoplay=1`;
        }
        return url;
    };

    // ============================================
    // BUILD OPTIONS
    // ============================================
    const buildSortedOptions = () => {
        const responseOptions = currentItem?.response_options || currentItem?.options || [];
        if (responseOptions.length === 0) return [];

        const sorted = [...responseOptions].sort((a, b) => {
            const aVal = a.response_value || a.value || '';
            const bVal = b.response_value || b.value || '';
            return aVal.localeCompare(bVal);
        });

        return sorted.map(opt => {
            const letter = opt.response_value || opt.value || '';
            let displayText = null;

            if (language === 'kn') {
                displayText =
                    opt.display_text_kn ||
                    opt.response_text_kn ||
                    opt.text_kn ||
                    opt.kannada_text ||
                    opt.translations?.kn ||
                    null;
            }
            if (!displayText && language === 'en') {
                displayText =
                    opt.display_text_en ||
                    opt.response_text_en ||
                    opt.translations?.en ||
                    null;
            }
            if (!displayText) {
                displayText =
                    opt.display_text ||
                    opt.response_text ||
                    opt.response_label ||
                    opt.label ||
                    null;
            }
            if (!displayText) displayText = letter;

            return {
                value: letter,
                display: displayText,
                score: opt.score_weight || opt.score || 0,
            };
        });
    };

    const options = buildSortedOptions();
    const totalOptions = options.length || 0;

    // ⭐ Simple mode: 1 or 2 options → only video + options, no reset, auto-advance
    const simpleMode = totalOptions > 0 && totalOptions <= 2;

    // ============================================
    // LOAD SELECTIONS
    // ============================================
    useEffect(() => {
        const loadSelections = async () => {
            if (!currentItem?.id) return;
            try {
                setIsLoading(true);
                const response = await getVideoSelections(accessToken, currentItem.id);

                if (response.data.success && response.data.selections) {
                    const selectionMap = {};
                    response.data.selections.forEach(sel => {
                        selectionMap[sel.selection_order] = sel.response_value;
                    });
                    setSelections(selectionMap);
                    setCompletedSelections(response.data.selections.map(s => s.selection_order));
                    setIsComplete(
                        totalOptions > 0 &&
                        response.data.selections.length === totalOptions
                    );
                } else {
                    setSelections({});
                    setCompletedSelections([]);
                    setIsComplete(false);
                }
            } catch (error) {
                console.error('Error loading selections:', error);
                setSelections({});
                setCompletedSelections([]);
                setIsComplete(false);
            } finally {
                setIsLoading(false);
            }
        };
        loadSelections();
    }, [currentItem?.id, accessToken, totalOptions]);

    // ============================================
    // ORDER SELECTION
    // ============================================
    const handleOrderSelect = async (order, value, score) => {
        if (isComplete) {
            showInfo(t('test.already_ranked_all'));
            return;
        }
        if (selections[order]) {
            showWarning(t('test.already_selected_this_order', { ordinal: getOrdinal(order) }));
            return;
        }
        try {
            const response = await saveVideoSelection(accessToken, {
                item_id: currentItem.id,
                response_value: value,
                selection_order: order,
                score_weight: score,
            });
            setSelections(prev => ({ ...prev, [order]: value }));
            setCompletedSelections(prev => [...prev, order]);

            const completeFromServer = response.data.is_complete;
            const completeLocally =
                totalOptions > 0 &&
                (completedSelections.length + 1) === totalOptions;

            if (completeFromServer || completeLocally) {
                setIsComplete(true);
                const first = (response.data.all_selections || []).find(
                    s => s.selection_order === 1
                );
                if (first && onAnswerComplete) onAnswerComplete(first.response_value);
            } else if (simpleMode) {
                // ⭐ In simple mode, always fire the answer callback on pick —
                //    even if the backend doesn't return is_complete.
                if (onAnswerComplete) onAnswerComplete(value);
            }
        } catch (error) {
            console.error('Error saving video selection:', error);
            showError(t('test.error_saving'));
        }
    };

    const getOrdinal = (order) => {
        switch (order) {
            case 1: return t('test.rank_1st');
            case 2: return t('test.rank_2nd');
            case 3: return t('test.rank_3rd');
            case 4: return t('test.rank_4th');
            case 5: return t('test.rank_5th') || '5th';
            case 6: return t('test.rank_6th') || '6th';
            case 7: return t('test.rank_7th') || '7th';
            case 8: return t('test.rank_8th') || '8th';
            default: return `${order}`;
        }
    };

    const getNextOrder = () => {
        for (let i = 1; i <= totalOptions; i++) {
            if (!selections[i]) return i;
        }
        return null;
    };

    const nextOrder = getNextOrder();
    const embedUrl = getVideoUrl(currentItem, currentItem?.assessment_id);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ff9f2f]"></div>
                <span className="ml-3 text-gray-500">{t('common.loading')}</span>
            </div>
        );
    }

    // ============================================
    // RENDER
    // ============================================
    return (
        <div className="w-full pb-0 sm:pb-6">

            {/* VIDEO */}
            <div className="w-full mb-3 sm:mb-4 bg-black rounded-lg overflow-hidden
                h-[40vh] sm:h-auto
                sm:aspect-video">
                {embedUrl ? (
                    <div className="w-full h-full relative">
                        {currentItem?.video_name ? (
                            <video
                                key={currentItem.id}
                                src={embedUrl}
                                className="absolute inset-0 w-full h-full"
                                controls
                                autoPlay
                                playsInline
                                controlsList="nodownload"
                                disablePictureInPicture
                                onContextMenu={(e) => e.preventDefault()}
                                onError={(e) => {
                                    console.error('Video failed:', e);
                                    showError(t('video.drive_error') || 'Video failed to load');
                                }}
                            >
                                {t('video.no_support') || 'Your browser does not support the video tag.'}
                            </video>
                        ) : (
                            <iframe
                                src={embedUrl}
                                className="absolute top-0 left-0 w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                title={t('test.video_title') || 'Assessment Video'}
                            />
                        )}
                    </div>
                ) : (
                    <div className="w-full h-48 sm:h-64 bg-gray-800 flex items-center justify-center">
                        <p className="text-white text-sm">{t('video.no_video')}</p>
                    </div>
                )}
            </div>

            {/* ============================================ */}
            {/* ⭐ SIMPLE MODE (1–2 options)                  */}
            {/*    - No progress bar, no reset, no submit    */}
            {/*    - Pick an option → locks + advances       */}
            {/* ============================================ */}
            {simpleMode ? (
                <div className="space-y-2.5 sm:space-y-3 w-full">
                    {options.map((option, idx) => {
                        const selectedOrder = Object.keys(selections).find(
                            key => selections[key] === option.value
                        );
                        const isSelected = !!selectedOrder;
                        const anySelected = completedSelections.length > 0;

                        return (
                            <button
                                key={`${currentItem.id}-${option.value}-${idx}`}
                                onClick={() => {
                                    // Only allow picking once, and only if nothing
                                    // is picked yet for this item.
                                    if (anySelected) return;
                                    handleOrderSelect(1, option.value, option.score);
                                }}
                                disabled={anySelected || isSubmitting}
                                className={`
                                    w-full p-3 sm:p-4 text-left border-2 rounded-lg transition-all
                                    flex items-center gap-3
                                    ${isSelected
                                        ? 'bg-green-50 border-green-500'
                                        : anySelected
                                            ? 'bg-gray-100 border-gray-300 cursor-not-allowed opacity-60'
                                            : 'bg-white border-gray-200 hover:border-[#ff9f2f] hover:bg-gray-50'
                                    }
                                    ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}
                                `}
                            >
                                <span
                                    className={`
                                        flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full 
                                        flex items-center justify-center
                                        font-bold text-sm sm:text-base
                                        ${isSelected ? 'bg-green-500 text-white' : 'bg-[#063b56] text-white'}
                                    `}
                                >
                                    {option.value}
                                </span>

                                <div className="flex-1 min-w-0">
                                    <p className="text-sm sm:text-base font-medium text-gray-800 leading-snug">
                                        {option.display}
                                    </p>
                                </div>

                                {isSelected && (
                                    <span className="
                                        flex-shrink-0 text-[11px] sm:text-sm font-bold text-green-700
                                        bg-green-100 px-2 py-0.5 rounded whitespace-nowrap
                                    ">
                                        ✓
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
            ) : (
                <>
                    {/* ============================================ */}
                    {/* FULL MODE (3+ options) — unchanged           */}
                    {/* ============================================ */}

                    {/* RANKING PROGRESS + RESET */}
                    <div className="w-full mb-3">
                        <div className="flex justify-between items-center mb-1.5 gap-2">
                            <span className="text-[11px] sm:text-xs font-medium text-gray-700 truncate">
                                {t('test.ranking_progress')}
                            </span>
                            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                                <span className="text-[11px] sm:text-xs text-gray-500">
                                    {completedSelections.length}/{totalOptions}
                                </span>
                                <button
                                    onClick={handleReset}
                                    disabled={
                                        isResetting ||
                                        isSubmitting ||
                                        completedSelections.length === 0
                                    }
                                    className={`
                                        flex items-center gap-1 text-[10px] sm:text-xs font-medium
                                        px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-md border transition-all
                                        ${completedSelections.length === 0 || isSubmitting
                                            ? 'text-gray-300 border-gray-200 cursor-not-allowed'
                                            : 'text-red-600 border-red-200 hover:bg-red-50'
                                        }
                                    `}
                                    title={t('test.reset_tooltip') || 'Reset all selections for this question'}
                                >
                                    {isResetting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-500"></div>
                                            <span className="hidden sm:inline">{t('test.resetting') || 'Resetting...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <RefreshIcon className="h-3 w-3" />
                                            <span>{t('test.reset') || 'Reset'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                                className="bg-[#ff9f2f] h-1.5 rounded-full transition-all duration-300"
                                style={{
                                    width: totalOptions > 0
                                        ? `${(completedSelections.length / totalOptions) * 100}%`
                                        : '0%'
                                }}
                            />
                        </div>
                    </div>

                    {/* STATUS MESSAGE */}
                    <div className="w-full mb-3">
                        {isComplete ? (
                            <div className="p-2 sm:p-2.5 bg-green-50 rounded-lg border border-green-200 text-center">
                                <span className="text-green-600 text-xs sm:text-sm font-medium">
                                    ✅ {t('test.all_ranked', { count: totalOptions })}
                                </span>
                            </div>
                        ) : (
                            <div className="p-2 sm:p-2.5 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-[11px] sm:text-xs text-blue-600 text-center">
                                    📹 {t('test.instruction_rank', { ordinal: getOrdinal(nextOrder) })}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* OPTIONS */}
                    <div className="space-y-2.5 sm:space-y-3 w-full">
                        {options.length === 0 ? (
                            <div className="p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center text-yellow-700 text-sm">
                                {t('test.no_options')}
                            </div>
                        ) : (
                            options.map((option, idx) => {
                                const selectedOrder = Object.keys(selections).find(
                                    key => selections[key] === option.value
                                );
                                const isSelected = !!selectedOrder;
                                const orderNumber = selectedOrder ? parseInt(selectedOrder) : null;

                                return (
                                    <button
                                        key={`${currentItem.id}-${option.value}-${idx}`}
                                        onClick={() => {
                                            if (!isSelected && nextOrder) {
                                                handleOrderSelect(nextOrder, option.value, option.score);
                                            } else if (isSelected) {
                                                showInfo(
                                                    t('test.already_selected', { ordinal: getOrdinal(orderNumber) })
                                                );
                                            }
                                        }}
                                        disabled={isComplete || isSelected || isSubmitting || isResetting}
                                        className={`
                                            w-full p-2.5 sm:p-4 text-left border-2 rounded-lg transition-all
                                            flex items-center gap-2 sm:gap-3
                                            ${isSelected
                                                ? 'bg-green-50 border-green-500'
                                                : isComplete
                                                    ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                                                    : 'bg-white border-gray-200 hover:border-[#ff9f2f] hover:bg-gray-50'
                                            }
                                            ${(isSubmitting || isResetting) ? 'opacity-50 cursor-not-allowed' : ''}
                                        `}
                                    >
                                        <span
                                            className={`
                                                flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full 
                                                flex items-center justify-center
                                                font-bold text-sm sm:text-base
                                                ${isSelected ? 'bg-green-500 text-white' : 'bg-[#063b56] text-white'}
                                            `}
                                        >
                                            {option.value}
                                        </span>

                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm sm:text-base font-medium text-gray-800 leading-snug">
                                                {option.display}
                                            </p>
                                        </div>

                                        {isSelected && (
                                            <span className="
                                                flex-shrink-0 text-[11px] sm:text-sm font-bold text-green-700
                                                bg-green-100 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded whitespace-nowrap
                                            ">
                                                ✓ {getOrdinal(orderNumber)}
                                            </span>
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>

                    {/* SUBMIT */}
                    {allQuestionsAnswered && (
                        <div className="w-full mt-4">
                            {isSubmitting ? (
                                <div className="w-full py-3 rounded-lg bg-gray-500 flex items-center justify-center gap-2">
                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                    <span className="text-white font-bold text-sm sm:text-base">
                                        {t('test.submitting') || 'Submitting...'}
                                    </span>
                                </div>
                            ) : autoSubmitCountdown !== null ? (
                                <div className="w-full py-3 px-3 rounded-lg bg-green-500 border-2 border-green-600 flex items-center justify-center gap-2 sm:gap-3 text-white">
                                    <span className="font-bold text-xs sm:text-base">
                                        {t('test.auto_submitting_in') || 'Submitting in'} {autoSubmitCountdown}s
                                    </span>
                                    <button
                                        onClick={handleManualSubmit}
                                        className="px-2 sm:px-3 py-1 bg-white text-green-700 text-[10px] sm:text-xs font-bold rounded whitespace-nowrap"
                                    >
                                        {t('test.submit_now') || 'Submit Now'}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={handleManualSubmit}
                                    className="w-full py-3 rounded-lg font-bold text-sm sm:text-base
                                        flex items-center justify-center gap-2
                                        bg-green-600 hover:bg-green-700 text-white"
                                >
                                    {t('test.submit') || 'Submit Assessment'}
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default VideoQuestion;